import { useRef, useState } from "react";
import { UploadCloud, FileCheck2, X } from "lucide-react";
import { NOMINEE_RELATIONS, fileToBase64 } from "../wizardUtils";

// Reusable drag/drop file field.
function FileDrop({ label, hint, fileName, onFile, onClear, error, testid }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const pick = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert("File must be under 5MB");
      return;
    }
    onFile(file);
  };

  return (
    <div>
      {label && <label className="form-label">{label}</label>}
      {fileName ? (
        <div
          className="flex items-center justify-between p-4 rounded-xl bg-base border border-subtle"
          data-testid={`${testid}-uploaded`}
        >
          <div className="flex items-center gap-3">
            <FileCheck2 className="text-[color:var(--accent)]" size={22} />
            <div>
              <p className="text-sm font-medium">{fileName}</p>
              <p className="text-xs text-muted-brand">Uploaded successfully</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClear}
            className="text-muted-brand hover:text-primary-brand"
            data-testid={`${testid}-remove`}
            aria-label="Remove file"
          >
            <X size={20} />
          </button>
        </div>
      ) : (
        <div
          className={`dropzone ${dragging ? "dragging" : ""}`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            pick(e.dataTransfer?.files?.[0]);
          }}
          data-testid={`${testid}-dropzone`}
          role="button"
          tabIndex={0}
        >
          <UploadCloud className="text-secondary-brand mb-3" size={34} />
          <p className="text-base font-semibold mb-1">
            Drop your file here, or click to browse
          </p>
          <p className="text-xs text-muted-brand">{hint}</p>
          <input
            ref={inputRef}
            type="file"
            accept=".pdf,image/*"
            hidden
            onChange={(e) => pick(e.target.files?.[0])}
            data-testid={`${testid}-input`}
          />
        </div>
      )}
      {error && (
        <p className="error-text mt-3" data-testid={`${testid}-error`}>
          {error}
        </p>
      )}
    </div>
  );
}

export default function KYC({ state, setState, errors }) {
  const [showPan, setShowPan] = useState(false);
  const [showAadhaar, setShowAadhaar] = useState(false);

  const setKyc = (field, value) =>
    setState((s) => ({ ...s, kyc: { ...s.kyc, [field]: value } }));
  const setNominee = (field, value) =>
    setState((s) => ({
      ...s,
      kyc: { ...s.kyc, nominee: { ...s.kyc.nominee, [field]: value } },
    }));

  // Returns a handler that stores a file under <prefix>FileName/Type/Base64.
  const setFile = (prefix) => async (file) => {
    const b64 = await fileToBase64(file);
    setState((s) => ({
      ...s,
      kyc: {
        ...s.kyc,
        [`${prefix}FileName`]: file.name,
        [`${prefix}FileType`]: file.type,
        [`${prefix}Base64`]: b64,
      },
    }));
  };
  const clearFile = (prefix) => () =>
    setState((s) => ({
      ...s,
      kyc: {
        ...s.kyc,
        [`${prefix}FileName`]: "",
        [`${prefix}FileType`]: "",
        [`${prefix}Base64`]: "",
      },
    }));

  const maskTail = (v, keep) =>
    v.length > keep ? "•".repeat(v.length - keep) + v.slice(-keep) : v;

  return (
    <div data-testid="wizard-step-kyc">
      <h2 className="text-3xl sm:text-4xl font-medium tracking-tight mb-2">
        Documents & KYC
      </h2>
      <p className="text-secondary-brand mb-8 leading-relaxed">
        Just a few final details to complete your submission.
      </p>

      {/* PAN */}
      <div className="bg-surface border border-subtle rounded-2xl p-6 mb-6">
        <h3 className="text-lg font-medium mb-4">PAN Card <span className="text-[color:var(--accent)]">*</span></h3>
        <div className="grid grid-cols-1 gap-5">
          <div>
            <label className="form-label" htmlFor="panNumber">PAN Number</label>
            <div className="relative">
              <input
                id="panNumber"
                type={showPan ? "text" : "password"}
                value={state.kyc.panNumber}
                onChange={(e) =>
                  setKyc("panNumber", e.target.value.toUpperCase().slice(0, 10))
                }
                className={`form-input pr-16 ${errors.panNumber ? "error" : ""}`}
                placeholder="ABCDE1234F"
                data-testid="input-pan-number"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowPan((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs uppercase tracking-widest text-muted-brand hover:text-primary-brand"
              >
                {showPan ? "Hide" : "Show"}
              </button>
            </div>
            {state.kyc.panNumber && !showPan && (
              <p className="text-xs text-muted-brand mt-1">
                Preview: {maskTail(state.kyc.panNumber, 3)}
              </p>
            )}
            {errors.panNumber && (
              <p className="error-text" data-testid="error-pan-number">{errors.panNumber}</p>
            )}
          </div>
          <FileDrop
            label="Upload PAN Card"
            hint="PDF, JPG, PNG · Max 5MB"
            fileName={state.kyc.panCardFileName}
            onFile={setFile("panCard")}
            onClear={clearFile("panCard")}
            error={errors.panCard}
            testid="pan-card"
          />
        </div>
      </div>

      {/* Aadhaar */}
      <div className="bg-surface border border-subtle rounded-2xl p-6 mb-6">
        <h3 className="text-lg font-medium mb-4">Aadhaar Card <span className="text-[color:var(--accent)]">*</span></h3>
        <div className="grid grid-cols-1 gap-5">
          <div>
            <label className="form-label" htmlFor="aadhaarNumber">Aadhaar Number</label>
            <div className="relative">
              <input
                id="aadhaarNumber"
                type={showAadhaar ? "text" : "password"}
                value={state.kyc.aadhaarNumber}
                onChange={(e) =>
                  setKyc("aadhaarNumber", e.target.value.replace(/\D/g, "").slice(0, 12))
                }
                className={`form-input pr-16 ${errors.aadhaarNumber ? "error" : ""}`}
                placeholder="12 digit Aadhaar"
                data-testid="input-aadhaar-number"
                autoComplete="off"
              />
              <button
                type="button"
                onClick={() => setShowAadhaar((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs uppercase tracking-widest text-muted-brand hover:text-primary-brand"
              >
                {showAadhaar ? "Hide" : "Show"}
              </button>
            </div>
            {state.kyc.aadhaarNumber && !showAadhaar && (
              <p className="text-xs text-muted-brand mt-1">
                Preview: {maskTail(state.kyc.aadhaarNumber, 4)}
              </p>
            )}
            {errors.aadhaarNumber && (
              <p className="error-text" data-testid="error-aadhaar-number">{errors.aadhaarNumber}</p>
            )}
          </div>
          <FileDrop
            label="Upload Aadhaar Card"
            hint="PDF, JPG, PNG · Max 5MB"
            fileName={state.kyc.aadhaarCardFileName}
            onFile={setFile("aadhaarCard")}
            onClear={clearFile("aadhaarCard")}
            error={errors.aadhaarCard}
            testid="aadhaar-card"
          />
        </div>
      </div>

      {/* Portability */}
      <div className="bg-surface border border-subtle rounded-2xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium">Porting an existing policy?</h3>
            <p className="text-sm text-muted-brand mt-1">
              Turn on if you're switching from another insurer.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={state.kyc.portEnabled}
            onClick={() => setKyc("portEnabled", !state.kyc.portEnabled)}
            data-testid="toggle-port"
            className="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition-colors"
            style={{
              backgroundColor: state.kyc.portEnabled ? "var(--accent)" : "#d1d5db",
              borderColor: state.kyc.portEnabled ? "var(--accent)" : "#9ca3af",
            }}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                state.kyc.portEnabled ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
        </div>

        {state.kyc.portEnabled && (
          <div className="mt-5">
            <FileDrop
              label="Upload Previous Policy"
              hint="PDF, JPG, PNG · Max 5MB · Your current/expiring policy document"
              fileName={state.kyc.prevPolicyFileName}
              onFile={setFile("prevPolicy")}
              onClear={clearFile("prevPolicy")}
              error={errors.prevPolicy}
              testid="prev-policy"
            />
          </div>
        )}
      </div>

      {/* Nominee */}
      <div className="bg-surface border border-subtle rounded-2xl p-6 mb-6">
        <h3 className="text-lg font-medium mb-4">Nominee Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div>
            <label className="form-label" htmlFor="nomineeName">Nominee Name</label>
            <input
              id="nomineeName"
              type="text"
              value={state.kyc.nominee.name}
              onChange={(e) => setNominee("name", e.target.value)}
              className={`form-input ${errors.nomineeName ? "error" : ""}`}
              placeholder="Full name"
              data-testid="input-nominee-name"
            />
            {errors.nomineeName && (
              <p className="error-text" data-testid="error-nominee-name">{errors.nomineeName}</p>
            )}
          </div>
          <div>
            <label className="form-label" htmlFor="nomineeRelation">Relation</label>
            <select
              id="nomineeRelation"
              value={state.kyc.nominee.relation}
              onChange={(e) => setNominee("relation", e.target.value)}
              className={`form-input ${errors.nomineeRelation ? "error" : ""}`}
              data-testid="select-nominee-relation"
            >
              <option value="">Select relation…</option>
              {NOMINEE_RELATIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {errors.nomineeRelation && (
              <p className="error-text" data-testid="error-nominee-relation">{errors.nomineeRelation}</p>
            )}
          </div>
          <div>
            <label className="form-label" htmlFor="nomineeDob">DOB</label>
            <input
              id="nomineeDob"
              type="date"
              value={state.kyc.nominee.dob}
              onChange={(e) => setNominee("dob", e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              className={`form-input ${errors.nomineeDob ? "error" : ""}`}
              data-testid="input-nominee-dob"
            />
            {errors.nomineeDob && (
              <p className="error-text" data-testid="error-nominee-dob">{errors.nomineeDob}</p>
            )}
          </div>
        </div>
      </div>

      {/* Medical Declaration */}
      <div className="bg-surface border border-subtle rounded-2xl p-6 mb-6">
        <h3 className="text-lg font-medium mb-4">Medical Declaration</h3>
        <div className="flex flex-col gap-3">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="med-decl-none">
            <input
              type="radio"
              name="medDecl"
              className="mt-1 accent-[color:var(--accent)]"
              checked={state.kyc.medicalDeclaration === "none"}
              onChange={() => setKyc("medicalDeclaration", "none")}
            />
            <span className="text-sm leading-relaxed">
              I declare that I <strong>do not have</strong> any pre-existing health conditions.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="med-decl-existing">
            <input
              type="radio"
              name="medDecl"
              className="mt-1 accent-[color:var(--accent)]"
              checked={state.kyc.medicalDeclaration === "existing"}
              onChange={() => setKyc("medicalDeclaration", "existing")}
            />
            <span className="text-sm leading-relaxed">
              I have a pre-existing condition (please describe below).
            </span>
          </label>

          {state.kyc.medicalDeclaration === "existing" && (
            <div className="mt-2">
              <label className="form-label" htmlFor="medcond">Please specify</label>
              <textarea
                id="medcond"
                rows={3}
                value={state.kyc.medicalConditions}
                onChange={(e) => setKyc("medicalConditions", e.target.value)}
                className={`form-input py-3 ${errors.medicalConditions ? "error" : ""}`}
                style={{ height: "auto" }}
                placeholder="e.g. Type 2 Diabetes since 2019, on Metformin."
                data-testid="input-medconditions"
              />
              {errors.medicalConditions && (
                <p className="error-text" data-testid="error-medconditions">{errors.medicalConditions}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Consent */}
      <div className="bg-surface border border-subtle rounded-2xl p-6">
        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-label">
          <input
            type="checkbox"
            checked={state.kyc.consent}
            onChange={(e) => setKyc("consent", e.target.checked)}
            className="mt-1 h-5 w-5 accent-[color:var(--accent)]"
            data-testid="input-consent"
          />
          <span className="text-sm leading-relaxed">
            I hereby authorize the insurer to contact me and use my data for policy
            issuance. I understand this consent is mandatory to complete the submission.
          </span>
        </label>
        {errors.consent && (
          <p className="error-text mt-2" data-testid="error-consent">{errors.consent}</p>
        )}
      </div>
    </div>
  );
}
